import { useLazyQuery } from '@apollo/client'
import ProductListingLevels from '@deltek/specification-client/core/Constants/productListingLevels'
import { NavPromptMessageProps } from '@deltek/specification-client/core/Forms/NavPrompt'
import NavPromptDialogContainer from '@deltek/specification-client/core/Forms/NavPromptDialogContainer'
import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { isEqual } from '@deltek/specification-client/core/Utilities/objectUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Checkbox } from '@material-ui/core'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import { AutoCompleteInput } from 'bpm/pages/ProductsPage/AddProductDialog/AutoCompleteInput'
import clsx from 'clsx'
import React, {
    ChangeEvent,
    FC,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'

import {
    ListingsGetListingProductTypeProperties,
    ProductSearchAutosuggestQuery,
} from 'api/graphQL/queries/product.queries'
import {
    ProductSearchAutosuggestResponse,
    SearchFacetInput,
} from 'api/graphQL/types/product.type'
import { GetListingDetail } from 'bpm/api/graphQL/queries/productSearch.queries'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'
import { ProductTypeAutocompleteOption } from '../../AddProductDialog/ProductTypeAutocompleteOption'
import { trimFamilyAndQuotesFromAutosuggestResponse } from '../../productsUtils'
import { CopyProductForm, ListingData } from './CopyDelete/CopyProduct'
import useStyles from './CopyMoveDialog.styles'
import CopyMoveModal from './CopyMoveModal/CopyMoveModal'
import {
    ListingHeaderVariants,
    ProductListingDetailForm,
} from './ListingDetailsPage'
import { blankForm, noChange } from './ListingHeader.constants'
import { ListingStatus } from './ListingHeaderVariants/ListingStatusVariant'

type CopyMoveDialogProps = {
    isLoading: boolean
    isBulkAction?: boolean
    bulkActionType?: string
    copyOrMoveModalOpen: boolean
    productInfo?: any
    setIsPDNModalOpen: (isOpen: boolean) => void
    handleCopyModalClose: VoidFunction
    setTempPDNStore: ({
        actionType,
        value,
    }: {
        actionType: string
        value: [ListingHeaderVariants | string, string | boolean] | never[]
    }) => void
    productListingDetailFormProp?: ProductListingDetailForm
    copyMoveProductForm: Partial<CopyProductForm>
    setCopyMoveProductForm: (form: Partial<CopyProductForm>) => void
}

const CopyMoveDialog: FC<CopyMoveDialogProps> = ({
    isLoading,
    isBulkAction,
    bulkActionType,
    copyOrMoveModalOpen,
    copyMoveProductForm,
    setCopyMoveProductForm,
    productInfo,
    setIsPDNModalOpen,
    setTempPDNStore,
    productListingDetailFormProp,
    handleCopyModalClose,
}) => {
    const classes = useStyles()
    const { showGlobalSnackbar } = useGlobalSnackbar()

    const currentAutoSuggest = useRef<string>('')

    const [autoSuggestResults, setAutoSuggestResults] = useState<
        Record<string, string[]>
    >({})
    const [sourceProductCount, setSourceProductCount] = useState(0)

    const [matchPropertiesCount, setMatchPropertiesCount] = useState(0)

    const stateItems = useMemo(() => {
        const { listingType: newListingType } = copyMoveProductForm
        if (!!newListingType?.value) {
            const defaultOptions = [noChange, ListingStatus.Completed]
            const listingType =
                newListingType?.value === noChange
                    ? productInfo?.type
                    : newListingType?.value || ''
            switch (listingType) {
                case ListingLevel.Premium: {
                    defaultOptions.splice(
                        1,
                        0,
                        ...[
                            ListingStatus.PremiumReview,
                            ListingStatus.PremiumReviewBPMSubmitted,
                        ]
                    )
                    break
                }
                case ListingLevel.PremiumMasterSpec: {
                    defaultOptions.splice(
                        1,
                        0,
                        ...[
                            ListingStatus.PremiumMasterSpecReview,
                            ListingStatus.PremiumMasterSpecReviewBPMSubmitted,
                        ]
                    )
                    break
                }
                case ListingLevel.PremiumProprietary: {
                    defaultOptions.splice(
                        1,
                        0,
                        ...[
                            ListingStatus.ProprietaryReview,
                            ListingStatus.ProprietaryReviewBPMSubmitted,
                        ]
                    )
                    break
                }
                case ListingLevel.Manufacturer: {
                    if (
                        productInfo?.type !== ListingLevel.Manufacturer &&
                        productInfo?.submissionStatus !==
                            ListingStatus.Completed
                    )
                        defaultOptions.splice(0, 1)
                }
                default:
                    defaultOptions
            }

            const stateOptions = defaultOptions.filter(
                (state) => state !== productInfo?.submissionStatus
            )

            const prevFormState = { ...copyMoveProductForm }

            setCopyMoveProductForm({
                ...prevFormState,
                state: { value: stateOptions[0], valid: true },
            })

            return stateOptions
        }
        return []
    }, [productInfo?.type, copyMoveProductForm.listingType])

    const listingTypeItems = useMemo(() => {
        const productListingLevels = ProductListingLevels.filter(
            (level) => level !== productInfo?.type
        )
        return [noChange, ...productListingLevels]
    }, [productInfo?.type])

    const [getListingDetail, { data: listingDetailData = {} }] = useLazyQuery<
        any
    >(GetListingDetail, {
        displayName: 'ProductListingDetailResult',
        fetchPolicy: 'no-cache',
        onCompleted: () => {},
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message: error?.message || 'Error on fetching data',
                    variant: 'error',
                    id: 'Product_listing_detail_result_error',
                },
            })
        },
    })

    const [
        getDestListingDetail,
        { data: destListingDetailData = {} },
    ] = useLazyQuery<any>(ListingsGetListingProductTypeProperties, {
        displayName: 'ListingProductTypePropertiesResult',
        fetchPolicy: 'no-cache',
        onCompleted: () => {
            if (!!productListingDetailFormProp) {
                let totalMatched = 0
                let values =
                    productListingDetailFormProp?.properties
                        ?.normalizedProperties
                let newValues = Object.keys(values)
                    ?.map((propertyName) => {
                        return values[propertyName]
                            .filter(({ optionGroups }) => {
                                let optionArray = optionGroups
                                    ?.map(({ options }) =>
                                        options?.filter(
                                            (option) => option.isSupported
                                        )
                                    )
                                    .flat(1)

                                return optionArray && optionArray.length > 0
                            })
                            ?.map(
                                ({ originalPropertyName, subPropertyName }) => {
                                    return (
                                        (originalPropertyName?.trim()
                                            ? originalPropertyName + '|'
                                            : '') + subPropertyName
                                    ).replaceAll(' / ', '|')
                                }
                            )
                    })
                    .flat(3)

                destListingDetailData?.listingsGetListingProductTypeProperties?.properties?.map(
                    ({
                        propertyId: propertyIdDest = '',
                        propertyName: propertyNameDest = '',
                    }) => {
                        if (newValues.includes(propertyNameDest)) totalMatched++
                    }
                )
                setMatchPropertiesCount(totalMatched)
            }
        },
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message: error?.message || 'Error on fetching data',
                    variant: 'error',
                    id: 'listing_product_type_properties_error',
                },
            })
        },
    })

    const [
        executeAutoSuggestion,
        { loading: autosuggestLoading, data: autosuggestData },
    ] = useLazyQuery<ProductSearchAutosuggestResponse>(
        ProductSearchAutosuggestQuery,
        {
            fetchPolicy: 'no-cache',
            onError: () => {
                showGlobalSnackbar({
                    variables: {
                        message: 'Something went wrong with autosuggest',
                        variant: 'error',
                        id: 'autosuggest_Error',
                    },
                })
            },
        }
    )

    const handleBulkAction = useCallback(() => {
        setTempPDNStore({
            actionType: bulkActionType === 'Move' ? 'MOVE' : 'COPY',
            value: [],
        })
        setIsPDNModalOpen(true)
    }, [bulkActionType, setTempPDNStore])

    const handleCopyOrMove = useCallback(
        (type) => () => {
            NavPromptDialogContainer(
                JSON.stringify({
                    title: '',
                    preferredMessage: `You are about to ${
                        !isBulkAction ? type : bulkActionType
                    } ${
                        !isBulkAction ? 'a product' : 'the selected product/s'
                    }, are you sure you want to continue?`,
                    buttonColor: '#d3455b',
                    confirmText: 'Okay',
                    cancelText: 'Cancel',
                } as NavPromptMessageProps),
                (callbackState: boolean) => {
                    if (!callbackState) return
                    if (!isBulkAction) {
                        setTempPDNStore({
                            actionType: type === 'Move' ? 'MOVE' : 'COPY',
                            value: [],
                        })

                        setIsPDNModalOpen(true)
                    } else {
                        handleBulkAction()
                    }
                }
            )
        },
        []
    )

    const handleBulkActionCancel = useCallback(() => {
        setCopyMoveProductForm({ ...blankForm })
        handleCopyModalClose?.()
    }, [handleCopyModalClose])

    const callAutoSuggestQuery = useCallback(
        (
            searchField: string,
            value: string,
            validateFamily = false,
            validateProductTypeName = false
        ) => {
            if (
                searchField === 'sectionNames' ||
                searchField === 'productType'
            ) {
                currentAutoSuggest.current =
                    validateFamily || validateProductTypeName
                        ? validateFamily
                            ? 'validateFamily'
                            : 'validateProductTypeName'
                        : searchField
                const autoPayload = {
                    input: {
                        searchLimit: 50,
                        facets: [] as Partial<SearchFacetInput>[],
                        searchField:
                            searchField !== 'productType'
                                ? searchField
                                : 'sectionNameWithProductTypeWithPremiumPlusWithPtid',
                        searchText: value ?? '',
                    },
                }

                if (searchField === 'productType')
                    autoPayload.input.facets = [
                        {
                            name: 'listingLevels',
                            values: [''],
                        },
                        {
                            name: 'sectionNames',
                            values: [
                                copyMoveProductForm.sectionNames?.value || '',
                            ],
                        },
                    ]
                executeAutoSuggestion({
                    variables: autoPayload,
                })
            }
        },
        [executeAutoSuggestion, currentAutoSuggest, copyMoveProductForm]
    )

    const validateNewFamily = useCallback(
        (key: string, value: string) => {
            let sectionNames = ''
            let isValid = true
            if (key === 'sectionNumber') {
                const familyNumber = value?.split('.')
                isValid =
                    familyNumber[0]?.trim()?.length === 6 &&
                    !isNaN(+familyNumber[0]) &&
                    (!familyNumber[1] ||
                        (familyNumber[1]?.trim()?.length <= 2 &&
                            !isNaN(+familyNumber[1])))
            }
            callAutoSuggestQuery('sectionNames', sectionNames, true)
            return isValid
        },
        [callAutoSuggestQuery, copyMoveProductForm]
    )

    const columnValueChangeHandler = useCallback(
        (key: string, value: string | string[]) => {
            const isNonEmpty = !!(
                value &&
                ((typeof value === 'string' && value.trim()) ||
                    (typeof value === 'object' && value.length > 0))
            )
            const prevFormState = { ...copyMoveProductForm }

            const isNewFamilyValid =
                key === 'sectionNumber' &&
                validateNewFamily(key, value as string)

            const isvalid =
                key === 'sectionNumber' ? isNewFamilyValid : isNonEmpty

            let newState = prevFormState
            if (key === 'sectionNames') {
                newState = {
                    ...prevFormState,
                    productType: { value: '', valid: true },
                    specAgentProductTypeId: { value: '', valid: true },
                    [key]: {
                        value: (value as string).trim(),
                        valid: isvalid,
                    },
                }
            } else if (key === 'productTypeWithId') {
                const newPT = value[0]
                const newPtId = value[1]
                newState = {
                    ...prevFormState,
                    productType: { value: newPT, valid: true },
                    specAgentProductTypeId: { value: newPtId, valid: true },
                }
            } else {
                newState = {
                    ...prevFormState,
                    [key]: {
                        value: value,
                        valid: isvalid,
                    },
                }
            }

            if (!isEqual(copyMoveProductForm, newState)) {
                setCopyMoveProductForm(newState)
            }
        },
        [
            copyMoveProductForm,
            callAutoSuggestQuery,
            validateNewFamily,
            setCopyMoveProductForm,
        ]
    )

    const handleProductTypeChange = useCallback(
        (_: ChangeEvent<{}>, value: string | null) => {
            const delimitedOption = value?.split('%|%')
            const newVal =
                delimitedOption && delimitedOption?.length > 0
                    ? delimitedOption[0]
                    : ''
            const newPtid =
                delimitedOption && delimitedOption?.length > 2
                    ? delimitedOption[2]
                    : ''
            columnValueChangeHandler('productTypeWithId', [newVal, newPtid])
        },
        [columnValueChangeHandler]
    )

    const handleAutocompleteChange = useCallback(
        (key: string) => (_: ChangeEvent<{}>, value: string | null) => {
            console.timeLog('handleAutocompleteChange', value)
            columnValueChangeHandler(key, value ?? '')
        },
        [columnValueChangeHandler]
    )

    const handleOpen = useCallback(
        (searchField = '') => {
            callAutoSuggestQuery(searchField, '')
        },
        [callAutoSuggestQuery, copyMoveProductForm]
    )

    const handleInputChange = useCallback(
        (event: ChangeEvent<{}>, val: string) => {
            const targetInput = event?.currentTarget as HTMLInputElement
            const searchField = targetInput?.name

            callAutoSuggestQuery(searchField || 'productType', val)
        },
        [callAutoSuggestQuery]
    )

    const handleAutocompleteOpen = useCallback(
        (searchField: string) => (_: ChangeEvent<{}>) => {
            handleOpen?.(searchField)
        },
        [handleInputChange]
    )

    const handleIncludeManufacturerOptions = useCallback(() => {
        columnValueChangeHandler(
            'includeManufacturerOptions',
            `${
                copyMoveProductForm.includeManufacturerOptions?.value === 'true'
                    ? 'false'
                    : 'true'
            }`
        )
    }, [
        columnValueChangeHandler,
        copyMoveProductForm.includeManufacturerOptions,
    ])

    useEffect(() => {
        if (!autosuggestData) {
            return
        }

        const hasAutosuggestResults =
            (autosuggestData?.listingAutoSuggest.result ?? []).length > 0

        // Set these so that autosuggest setting of options will not loop
        if (
            currentAutoSuggest.current &&
            (hasAutosuggestResults ||
                (!hasAutosuggestResults &&
                    autoSuggestResults[currentAutoSuggest.current]))
        ) {
            if (
                currentAutoSuggest.current === 'sectionNames' &&
                !isEqual(
                    autoSuggestResults[currentAutoSuggest.current],
                    autosuggestData?.listingAutoSuggest.result
                )
            ) {
                setAutoSuggestResults({
                    [currentAutoSuggest.current]:
                        autosuggestData?.listingAutoSuggest.result ?? [],
                })
                return
            }

            if (
                currentAutoSuggest.current === 'productType' &&
                !isEqual(
                    autoSuggestResults[currentAutoSuggest.current],
                    trimFamilyAndQuotesFromAutosuggestResponse(
                        autosuggestData?.listingAutoSuggest.result ?? []
                    )
                )
            ) {
                setAutoSuggestResults({
                    [currentAutoSuggest.current]:
                        trimFamilyAndQuotesFromAutosuggestResponse(
                            autosuggestData?.listingAutoSuggest.result ?? []
                        ) ?? [],
                })
                return
            }
        }
    }, [
        autosuggestData,
        showGlobalSnackbar,
        copyMoveProductForm,
        listingDetailData,
        autoSuggestResults,
    ])

    useEffect(() => {
        if (productInfo?.listingId && !isBulkAction) {
            getListingDetail({
                variables: {
                    input: {
                        listingId: productInfo?.listingId || '',
                        logEventSource: 'CopyMoveListing',
                    },
                },
            })
        }
    }, [productInfo?.listingId])

    useEffect(() => {
        if (copyMoveProductForm.productType?.value && !isBulkAction) {
            getDestListingDetail({
                variables: {
                    input: {
                        productType: copyMoveProductForm.productType?.value,
                        sectionName: copyMoveProductForm.sectionNames?.value,
                    },
                },
            })
        }
    }, [copyMoveProductForm])

    useEffect(() => {
        if (
            productListingDetailFormProp?.properties?.normalizedProperties &&
            !isBulkAction
        ) {
            setSourceProductCount(
                Object.keys(
                    productListingDetailFormProp?.properties
                        ?.normalizedProperties
                )
                    ?.map((propertyName) => {
                        return productListingDetailFormProp?.properties
                            ?.normalizedProperties[propertyName]
                    })
                    .flat(1).length
            )
        }
    }, [productListingDetailFormProp])

    const confirmationDialogContent = useMemo(() => {
        // need to fix this
        const productInfotemp: any = productInfo

        const listingDataKeys = !isBulkAction
            ? ListingData
            : ListingData.filter(
                  ({ key }) =>
                      ![
                          'isActive',
                          'firmName',
                          'model',
                          'categories',
                          'submissionStatus',
                          'state',
                          'type',
                          'productName',
                      ].includes(key)
              )

        return listingDataKeys?.map(
            ({ name, key }: { name: string; key: string }, index: number) => (
                <GridContainer
                    style={{ marginBottom: pxToRem(20) }}
                    key={`${index}-${key}`}
                >
                    <GridItem
                        style={{
                            width: pxToRem(160),
                            fontSize: '1.5rem',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            display: 'flex',
                            marginRight: '10px',
                            textAlign: 'end',
                        }}
                    >
                        {name}:
                    </GridItem>
                    <GridItem
                        style={{
                            maxWidth: pxToRem(500),
                            width: '100%',
                            wordBreak: 'break-all',
                        }}
                    >
                        {key === 'family' ? (
                            <GridItem
                                className={clsx(
                                    classes.rowContainer,
                                    'rowContainer'
                                )}
                                xs={12}
                                style={{ width: '100%' }}
                                data-testid="sectionNames"
                            >
                                <AutoCompleteInput
                                    name="sectionNames"
                                    label=""
                                    valid={
                                        copyMoveProductForm.sectionNames?.valid
                                    }
                                    handleOpen={handleAutocompleteOpen(
                                        'sectionNames'
                                    )}
                                    handleChange={handleAutocompleteChange(
                                        'sectionNames'
                                    )}
                                    handleInputChange={handleInputChange}
                                    selectedValue={
                                        (copyMoveProductForm.sectionNames
                                            ?.value ?? '') as string
                                    }
                                    loading={autosuggestLoading}
                                    items={
                                        autoSuggestResults.sectionNames ?? []
                                    }
                                    disabled={isLoading}
                                />
                            </GridItem>
                        ) : key === 'newProductType' ? (
                            <GridItem
                                style={{ width: '100%' }}
                                className={clsx(
                                    classes.rowContainer,
                                    'rowContainer'
                                )}
                                data-testid="productType"
                                xs={12}
                            >
                                <AutoCompleteInput
                                    name="productType"
                                    label=""
                                    disabled={
                                        !copyMoveProductForm.sectionNames
                                            ?.value || isLoading
                                    }
                                    valid={
                                        copyMoveProductForm.productType?.valid
                                    }
                                    handleOpen={handleAutocompleteOpen(
                                        'productType'
                                    )}
                                    handleChange={handleProductTypeChange}
                                    handleInputChange={handleInputChange}
                                    selectedValue={
                                        (copyMoveProductForm.productType
                                            ?.value ?? '') as string
                                    }
                                    loading={autosuggestLoading}
                                    items={autoSuggestResults.productType ?? []}
                                    customOptions={(option: string) => (
                                        <ProductTypeAutocompleteOption
                                            option={option}
                                        />
                                    )}
                                    getOptionLabel={(option) =>
                                        option?.split('%|%')[0] ?? ''
                                    }
                                />
                            </GridItem>
                        ) : key === 'newIsActive' ? (
                            <GridItem
                                style={{ width: '100%' }}
                                className={clsx(
                                    classes.rowContainer,
                                    'rowContainer'
                                )}
                                data-testid="productType"
                                xs={12}
                            >
                                <AutoCompleteInput
                                    name="isActive"
                                    label=""
                                    disabled={isLoading}
                                    valid={true}
                                    // handleOpen={() => handleOpen?.('isActive')}
                                    handleChange={(
                                        _e: ChangeEvent<{}>,
                                        value: string | null
                                    ) =>
                                        columnValueChangeHandler(
                                            'isActive',
                                            value ?? ''
                                        )
                                    }
                                    handleInputChange={handleInputChange}
                                    selectedValue={
                                        (copyMoveProductForm.isActive?.value ??
                                            '') as string
                                    }
                                    items={['Active', 'Inactive']}
                                />
                            </GridItem>
                        ) : key === 'isActive' ? (
                            productInfo?.isActive ? (
                                'Active'
                            ) : (
                                'Inactive'
                            )
                        ) : key === 'categories' ? (
                            productInfotemp?.categories?.join(', ') || ''
                        ) : key === 'includeManufacturerOptions' ? (
                            <GridItem
                                style={{ width: '100%' }}
                                className={clsx(
                                    classes.rowContainer,
                                    'rowContainer'
                                )}
                                data-testid="includeManufacturerOptions"
                            >
                                <Checkbox
                                    checked={
                                        copyMoveProductForm
                                            .includeManufacturerOptions
                                            ?.value === 'true'
                                    }
                                    color="default"
                                    icon={
                                        <CheckBoxOutlineBlankIcon fontSize="small" />
                                    }
                                    checkedIcon={
                                        <CheckBoxIcon fontSize="small" />
                                    }
                                    name="includeManufacturerOptions"
                                    onClick={handleIncludeManufacturerOptions}
                                />
                            </GridItem>
                        ) : key === 'newListingType' ? (
                            <GridItem
                                style={{ width: '100%' }}
                                className={clsx(
                                    classes.rowContainer,
                                    'rowContainer'
                                )}
                                data-testid="listingType"
                            >
                                <AutoCompleteInput
                                    name="listingType"
                                    label=""
                                    disabled={isLoading}
                                    valid={
                                        copyMoveProductForm.listingType?.valid
                                    }
                                    handleOpen={handleAutocompleteOpen(
                                        'listingType'
                                    )}
                                    handleChange={handleAutocompleteChange(
                                        'listingType'
                                    )}
                                    handleInputChange={handleInputChange}
                                    selectedValue={
                                        (copyMoveProductForm.listingType
                                            ?.value ?? '') as string
                                    }
                                    loading={autosuggestLoading}
                                    items={listingTypeItems ?? []}
                                />
                            </GridItem>
                        ) : key === 'newState' ? (
                            <GridItem
                                style={{ width: '100%' }}
                                className={clsx(
                                    classes.rowContainer,
                                    'rowContainer'
                                )}
                                data-testid="state"
                            >
                                <AutoCompleteInput
                                    name="state"
                                    label=""
                                    disabled={isLoading}
                                    valid={copyMoveProductForm.state?.valid}
                                    handleOpen={handleAutocompleteOpen('state')}
                                    handleChange={handleAutocompleteChange(
                                        'state'
                                    )}
                                    handleInputChange={handleInputChange}
                                    selectedValue={
                                        (copyMoveProductForm.state?.value ??
                                            '') as string
                                    }
                                    loading={autosuggestLoading}
                                    items={stateItems ?? []}
                                />
                            </GridItem>
                        ) : (
                            productInfotemp?.[key]
                        )}
                    </GridItem>
                </GridContainer>
            )
        )
    }, [
        autosuggestData,
        autosuggestLoading,
        autoSuggestResults,
        copyMoveProductForm,
        productInfo,
    ])

    return (
        <CopyMoveModal
            isBulkAction={isBulkAction}
            dialogHeading={
                !!isBulkAction
                    ? bulkActionType === 'Copy'
                        ? 'Bulk Copy Products'
                        : 'Bulk Move Products'
                    : undefined
            }
            confirmText={
                !!isBulkAction
                    ? bulkActionType === 'Copy'
                        ? 'Copy'
                        : 'Move'
                    : undefined
            }
            cancelText={!!isBulkAction ? 'Cancel' : undefined}
            openDialog={copyOrMoveModalOpen}
            handleDialogClose={handleCopyModalClose}
            dialogActionCancelCallback={
                !isBulkAction
                    ? handleCopyOrMove('Copy')
                    : handleBulkActionCancel
            }
            dialogActionCallback={
                !isBulkAction
                    ? handleCopyOrMove('Move')
                    : handleCopyOrMove(bulkActionType)
            }
            contentNode={confirmationDialogContent}
            removeHeaderBorder
            disabled={
                !copyMoveProductForm.sectionNames?.value ||
                !copyMoveProductForm.productType?.value
            }
            loading={isLoading}
            size="small"
            auditDetails={
                !isBulkAction
                    ? {
                          sourceProductCount: sourceProductCount,
                          matchPropertiesCount: matchPropertiesCount,
                      }
                    : undefined
            }
        />
    )
}

export default CopyMoveDialog
